import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { api, ApiError, type MeResponse } from "@/lib/api";

type AuthStatus = "loading" | "anonymous" | "authenticated";

export type AuthState = {
  status: AuthStatus;
  user: MeResponse | null;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  logoutAll: () => Promise<void>;
  reload: () => Promise<void>;
};

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<AuthStatus>("loading");
  const [user, setUser] = useState<MeResponse | null>(null);

  const reload = useCallback(async () => {
    try {
      const me = await api.me();
      setUser(me);
      setStatus("authenticated");
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        setUser(null);
        setStatus("anonymous");
        return;
      }
      // Unknown failure: treat as anonymous so the login screen appears.
      setUser(null);
      setStatus("anonymous");
    }
  }, []);

  useEffect(() => {
    // On boot, make sure a CSRF cookie exists so the first mutating
    // request has something to echo back, then try to resume a session.
    let cancelled = false;
    (async () => {
      try {
        await api.getCsrf();
      } catch {
        // Ignore — non-fatal; reload() below will surface auth state.
      }
      if (!cancelled) await reload();
    })();
    return () => {
      cancelled = true;
    };
  }, [reload]);

  const login = useCallback(
    async (email: string, password: string) => {
      await api.login(email, password);
      await reload();
    },
    [reload],
  );

  const logout = useCallback(async () => {
    try {
      await api.logout();
    } finally {
      setUser(null);
      setStatus("anonymous");
    }
  }, []);

  const logoutAll = useCallback(async () => {
    try {
      await api.logoutAll();
    } finally {
      setUser(null);
      setStatus("anonymous");
    }
  }, []);

  const value = useMemo<AuthState>(
    () => ({ status, user, login, logout, logoutAll, reload }),
    [status, user, login, logout, logoutAll, reload],
  );
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error("useAuth must be used inside <AuthProvider>");
  }
  return ctx;
}
